import React, { useState } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import NumberInfo from 'views/Numbers/Details/common/NumberInfo';
import { BodyS } from 'components/Typography/Typography';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import styles from 'assets/jss/material-dashboard-pro-react/views/settingsSubpageStyle';
import { makeStyles } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { SimService } from 'services/sim/sim';
import { finalize } from 'rxjs/operators';
import DialogTermsAndConditions from 'views/Numbers/Details/Services/subcomponents/common/DialogTermsAndConditions';
import apiConfig from 'utils/api/config';
import QrCode from './QrCode';
import SuccessBox from 'components/SuccessBox/SuccessBox';
import { setToast } from 'redux/modules/toast/actions';
import { OperationContextEnum } from 'utils/api/payloads/omantelApi';
import { UsersService } from 'services/users/users';
import OTP from 'components/OTP/OTP';
import OtpTab from '../../Settings/subcomponents/ResetAdslPasswordDialog/subcomponents/OtpTab';
import { ERROR_VALIDATING_TOKEN } from 'utils/api/errors/errorCodes';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IReplaceESimCardDialogProps {
	isOpen: boolean;
	showDialog: () => void;
	hideDialog: () => void;
	phoneNumber: string;
	accountDetails: string;
	isEsim?: boolean;
	reload: () => void;
	msisdn: string;
}

interface IQrResponse {
	qrCode?: string;
	qrLink?: string;
	serialNumber?: string;
}

const ReplaceESimCardDialog = ({
	isOpen,
	showDialog,
	hideDialog,
	phoneNumber,
	accountDetails,
	isEsim = false,
	reload,
	msisdn,
}: IReplaceESimCardDialogProps) => {
	const [confirmationDialog, setConfirmationDialog] = useState<boolean>(false);
	const [esimQrData, setEsimQrData] = useState<IQrResponse | null>(null);
	const [loader, setLoader] = useState<boolean>(false);
	const [isChecked, setIsChecked] = useState<boolean>(false);

	/*for otp validations */
	const [isOTPStep, setOTPStep] = useState(false);
	const [otp, setOtp] = useState('');
	const [isIncorrectOtp, setIsInCorrectOtp] = useState(false);

	const classes = useStyles();
	const dispatch = useDispatch();

	const replace = () => {
		hideDialog();
		setConfirmationDialog(true);
	};

	const clearOTP = () => {
		setOtp('');
		setOTPStep(false);
		setIsInCorrectOtp(false);
	};

	const cancelConfirmation = () => {
		clearOTP();
		setConfirmationDialog(false);
		showDialog();
	};

	const parseQrCode = (data: string) => {
		return data.substring(10, data.length - 4);
	};

	const replaceSimCard = () => {
		dispatch(showLoadingIndicator());
		SimService.replaceEsim({
			msisdn: phoneNumber,
			otp,
		})
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
				})
			)
			.subscribe(
				({ response }) => {
					setEsimQrData({
						serialNumber: response?.serialNumber,
						qrCode: parseQrCode(response?.qrCode),
						qrLink: response?.url ? `${response?.url} ${response?.activationCode}` : '',
					});
					setConfirmationDialog(false);
					setOtp('');
					showDialog();
					reload();
				},
				(error) => {
					if (error.response.errorCode === ERROR_VALIDATING_TOKEN) {
						setIsInCorrectOtp(true);
					} else {
						dispatch(
							setToast({
								error: true,
								label: translate('numbers.esim.replacement.error'),
								actionType: translate('error'),
							})
						);
					}
				}
			);
	};

	const closeDialog = (): void => {
		clearOTP();
		setEsimQrData(null);
		setIsChecked(false);
		hideDialog();
	};

	const requestForOtp = () => {
		dispatch(showLoadingIndicator());
		setIsInCorrectOtp(false);
		setOtp('');
		UsersService.requestForOtp(OperationContextEnum.REPLACEESIM, msisdn)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(() => {
				setToast({
					error: false,
					label: translate('otp.token.sent'),
				});
				setOTPStep(true);
			});
	};

	const hideConfirmationDialog = () => {
		setConfirmationDialog(false);
		clearOTP();
	};

	return (
		<>
			<CustomDialog
				isOpen={isOpen}
				hideDialog={closeDialog}
				header={translate('numbers.esim.replacement')}
				dialogBody={
					<div>
						<ConditionalRender
							show={!!esimQrData}
							onTrue={() => (
								<div className={classes.successBoxWrapper}>
									<SuccessBox text={translate('numbers.esim.replacement.success')} />
								</div>
							)}
						/>
						<NumberInfo phoneNumber={phoneNumber} accountDetails={accountDetails} />
						<ConditionalRender
							show={!!esimQrData}
							onTrue={() => (
								<QrCode
									phoneNumber={phoneNumber}
									setLoader={setLoader}
									esimQrData={esimQrData}
									loader={loader}
									isEsim={true}
								/>
							)}
						/>
						<ConditionalRender
							show={!esimQrData}
							onTrue={() => (
								<div className={classes.resultDataContainer}>
									<DialogTermsAndConditions
										termsAndConditions={{
											type: 'link',
											url: apiConfig.links.esimSupportedDevices,
											textUrl: 'numbers.esim.replacement.supported.devices',
											acceptText: 'numbers.esim.replacement.supported.devices.accept',
										}}
										isChecked={isChecked}
										setIsChecked={setIsChecked}
										showCheckbox
										showError={false}
									/>
								</div>
							)}
						/>
					</div>
				}
				rightSideButtons={
					esimQrData
						? []
						: [
								{
									text: translate('cancel'),
									onClick: closeDialog,
									color: 'tertiary-light',
								},
								{
									text: translate('replace'),
									onClick: replace,
									color: 'primary-light',
									disabled: !isChecked,
								},
						  ]
				}
			/>
			<ConfirmationDialog
				isOpen={confirmationDialog}
				hideDialog={hideConfirmationDialog}
				header={`${translate('numbers.settings.replace.esim.card')}?`}
				dialogBody={
					<div className={classes.confirmationDialogBody}>
						{isOTPStep ? (
							<OtpTab
								otp={otp}
								setOtp={setOtp}
								sendOtp={requestForOtp}
								otpIncorrect={isIncorrectOtp}
								inOneInput={false}
								submitOTP={replaceSimCard}
							/>
						) : (
							<>
								<BodyS>{translate('numbers.settings.replace.esim.are.you.sure1')}</BodyS>
								<BodyS color="danger">
									{translate('numbers.settings.replace.esim.are.you.sure.note')}
								</BodyS>
								<ConditionalRender
									show={isEsim}
									onTrue={() => (
										<BodyS>{translate('numbers.settings.replace.esim.are.you.sure2')}</BodyS>
									)}
								/>
							</>
						)}
					</div>
				}
				buttons={[
					{
						text: translate('cancel'),
						onClick: cancelConfirmation,
						color: 'tertiary-light',
					},
					{
						text: isOTPStep ? translate('replace.it') : translate('next'),
						onClick: isOTPStep ? replaceSimCard : requestForOtp,
						color: 'primary-light',
						disabled: isOTPStep && otp.length !== 5,
					},
				]}
			/>
		</>
	);
};

export default ReplaceESimCardDialog;
